import { API, graphqlOperation } from "aws-amplify";
import {
  LLMModelCapability,
  LLMModelPricingSnapshot,
  LLMOpenRouterModel,
  ListLLMModelCapabilitiesQuery,
  ListLLMModelPricingSnapshotsQuery,
  ListLLMOpenRouterModelsQuery,
} from "../API";
import {
  listLLMModelCapabilities,
  listLLMModelPricingSnapshots,
  listLLMOpenRouterModels,
} from "../graphql/queries";

const PAGE_LIMIT = 1000;

export interface OpenRouterCatalogRecord {
  model: LLMOpenRouterModel;
  currentPricing: LLMModelPricingSnapshot | null;
  routingProviders: string[];
  capabilityBadges: string[];
}

async function fetchAllOpenRouterModels(): Promise<LLMOpenRouterModel[]> {
  const items: LLMOpenRouterModel[] = [];
  let nextToken: string | null | undefined = undefined;

  do {
    const response = (await API.graphql(
      graphqlOperation(listLLMOpenRouterModels, {
        limit: PAGE_LIMIT,
        nextToken,
      })
    )) as { data?: ListLLMOpenRouterModelsQuery };

    const pageItems = response.data?.listLLMOpenRouterModels?.items || [];
    items.push(...(pageItems.filter(Boolean) as LLMOpenRouterModel[]));
    nextToken = response.data?.listLLMOpenRouterModels?.nextToken;
  } while (nextToken);

  return items;
}

async function fetchCurrentOpenRouterPricingSnapshots(): Promise<LLMModelPricingSnapshot[]> {
  const items: LLMModelPricingSnapshot[] = [];
  let nextToken: string | null | undefined = undefined;

  do {
    const response = (await API.graphql(
      graphqlOperation(listLLMModelPricingSnapshots, {
        limit: PAGE_LIMIT,
        nextToken,
        filter: {
          isCurrent: { eq: true },
          source: { eq: "OPENROUTER" },
        },
      })
    )) as { data?: ListLLMModelPricingSnapshotsQuery };

    const pageItems = response.data?.listLLMModelPricingSnapshots?.items || [];
    items.push(...(pageItems.filter(Boolean) as LLMModelPricingSnapshot[]));
    nextToken = response.data?.listLLMModelPricingSnapshots?.nextToken;
  } while (nextToken);

  return items;
}

async function fetchAllModelCapabilities(): Promise<LLMModelCapability[]> {
  const items: LLMModelCapability[] = [];
  let nextToken: string | null | undefined = undefined;

  do {
    const response = (await API.graphql(
      graphqlOperation(listLLMModelCapabilities, {
        limit: PAGE_LIMIT,
        nextToken,
      })
    )) as { data?: ListLLMModelCapabilitiesQuery };

    const pageItems = response.data?.listLLMModelCapabilities?.items || [];
    items.push(...(pageItems.filter(Boolean) as LLMModelCapability[]));
    nextToken = response.data?.listLLMModelCapabilities?.nextToken;
  } while (nextToken);

  return items;
}

export async function fetchOpenRouterCatalog(): Promise<OpenRouterCatalogRecord[]> {
  const [models, snapshots, providerCapabilities] = await Promise.all([
    fetchAllOpenRouterModels(),
    fetchCurrentOpenRouterPricingSnapshots(),
    fetchAllModelCapabilities(),
  ]);

  const pricingByModelId = new Map<string, LLMModelPricingSnapshot>();

  for (const snapshot of snapshots) {
    if (!snapshot.modelId) {
      continue;
    }

    const existing = pricingByModelId.get(snapshot.modelId);
    if (!existing) {
      pricingByModelId.set(snapshot.modelId, snapshot);
      continue;
    }

    if ((snapshot.fetchedAt || "") > (existing.fetchedAt || "")) {
      pricingByModelId.set(snapshot.modelId, snapshot);
    }
  }

  const routingProvidersByModelId = new Map<string, Set<string>>();
  const capabilityBadgesByModelId = new Map<string, Set<string>>();

  for (const capability of providerCapabilities) {
    const modelId = capability.modelId;
    const value = capability.value?.trim();
    if (!modelId || !value) {
      continue;
    }

    if (capability.category === "ROUTING_PROVIDER") {
      if (!routingProvidersByModelId.has(modelId)) {
        routingProvidersByModelId.set(modelId, new Set<string>());
      }

      routingProvidersByModelId.get(modelId)?.add(value);
      continue;
    }

    let badgeLabel: string | null = null;
    if (capability.category === "INPUT_MODALITY") {
      badgeLabel = `In: ${value}`;
    } else if (capability.category === "OUTPUT_MODALITY") {
      badgeLabel = `Out: ${value}`;
    }

    if (badgeLabel) {
      if (!capabilityBadgesByModelId.has(modelId)) {
        capabilityBadgesByModelId.set(modelId, new Set<string>());
      }
      capabilityBadgesByModelId.get(modelId)?.add(badgeLabel);
    }
  }

  return models
    .map((model) => ({
      model,
      currentPricing: pricingByModelId.get(model.id) || null,
      routingProviders: Array.from(routingProvidersByModelId.get(model.id) || []).sort((a, b) =>
        a.localeCompare(b)
      ),
      capabilityBadges: Array.from(capabilityBadgesByModelId.get(model.id) || []).sort((a, b) =>
        a.localeCompare(b)
      ),
    }))
    .sort((a, b) => {
      const deprecatedA = a.model.isDeprecated ? 1 : 0;
      const deprecatedB = b.model.isDeprecated ? 1 : 0;
      if (deprecatedA !== deprecatedB) {
        return deprecatedA - deprecatedB;
      }

      const providerA = a.model.provider?.displayName || a.model.providerId || "";
      const providerB = b.model.provider?.displayName || b.model.providerId || "";
      const providerCompare = providerA.localeCompare(providerB);
      if (providerCompare !== 0) {
        return providerCompare;
      }

      return (a.model.name || "").localeCompare(b.model.name || "");
    });
}
